"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.planPlasticPanelCutouts = void 0;
const three_1 = require("three");
// eslint-disable-next-line import/no-unresolved
const clipper_lib_1 = __importDefault(require("clipper-lib"));
const scale = 1000000;
function toPath(points) {
    if (points.length < 3 || points.some((point) => !Number.isFinite(point.x) || !Number.isFinite(point.y))) {
        throw new Error('A Plastic Panel cutout contour has invalid coordinates.');
    }
    return points.map(({ x, y }) => ({ X: Math.round(x * scale), Y: Math.round(y * scale) }));
}
function toVectors(path) {
    return path.map(({ X, Y }) => new three_1.Vector2(X / scale, Y / scale));
}
function planPlasticPanelCutouts({ panelContour, extrusions, clearance, }) {
    if (!Number.isFinite(clearance) || clearance < 0) {
        throw new Error('Invalid Plastic Panel cutout clearance.');
    }
    const panelPath = toPath(panelContour);
    if (!extrusions.length) {
        return { outerContour: panelContour.map((point) => point.clone()), holeContours: [], notchesCount: 0 };
    }
    // Only extrusions that penetrate the panel body generate cutouts. Extrusions that are
    // outside or merely touch/border the edge (e.g. frame extrusions) must never cut into the panel.
    const penetratingExtrusions = extrusions.filter(({ footprint }) => {
        try {
            const testClipper = new clipper_lib_1.default.Clipper();
            testClipper.AddPath(panelPath, clipper_lib_1.default.PolyType.ptSubject, true);
            testClipper.AddPath(toPath(footprint), clipper_lib_1.default.PolyType.ptClip, true);
            const intersectionPaths = new clipper_lib_1.default.Paths();
            testClipper.Execute(clipper_lib_1.default.ClipType.ctIntersection, intersectionPaths);
            const intersectionArea = intersectionPaths.reduce((sum, path) => sum + Math.abs(clipper_lib_1.default.Clipper.Area(path)), 0) /
                (scale * scale);
            return intersectionArea > 1e-5;
        }
        catch (_a) {
            return false;
        }
    });
    if (!penetratingExtrusions.length) {
        return { outerContour: panelContour.map((point) => point.clone()), holeContours: [], notchesCount: 0 };
    }
    const offsetter = new clipper_lib_1.default.ClipperOffset();
    penetratingExtrusions.forEach(({ id, footprint }) => {
        try {
            // jtMiter produces sharp-cornered offsets, which is correct for rectilinear profiles.
            // For acute-angle extrusions, set MiterLimit on ClipperOffset to cap spike length.
            offsetter.AddPath(toPath(footprint), clipper_lib_1.default.JoinType.jtMiter, clipper_lib_1.default.EndType.etClosedPolygon);
        }
        catch (error) {
            throw new Error(`Extrusion ${id} has an invalid panel-crossing profile.`);
        }
    });
    const expandedProfiles = [];
    offsetter.Execute(expandedProfiles, Math.round(clearance * scale));
    if (!expandedProfiles.length) {
        throw new Error('Extrusion cutout profiles cannot be generated.');
    }
    const clipper = new clipper_lib_1.default.Clipper();
    clipper.AddPath(panelPath, clipper_lib_1.default.PolyType.ptSubject, true);
    clipper.AddPaths(expandedProfiles, clipper_lib_1.default.PolyType.ptClip, true);
    const panelResult = new clipper_lib_1.default.PolyTree();
    if (!clipper.Execute(clipper_lib_1.default.ClipType.ctDifference, panelResult)) {
        throw new Error('Plastic Panel cutout geometry is invalid.');
    }
    const pieces = clipper_lib_1.default.JS.PolyTreeToExPolygons(panelResult);
    if (pieces.length !== 1 || pieces[0].outer.length < 3) {
        throw new Error('Extrusion cutouts would split or consume the Plastic Panel.');
    }
    const intersection = new clipper_lib_1.default.Clipper();
    intersection.AddPaths(expandedProfiles, clipper_lib_1.default.PolyType.ptSubject, true);
    intersection.AddPath(panelPath, clipper_lib_1.default.PolyType.ptClip, true);
    const cutouts = [];
    intersection.Execute(clipper_lib_1.default.ClipType.ctIntersection, cutouts);
    return {
        outerContour: toVectors(pieces[0].outer),
        holeContours: pieces[0].holes.map(toVectors),
        notchesCount: cutouts.length,
    };
}
exports.planPlasticPanelCutouts = planPlasticPanelCutouts;
